import { z } from "zod";

/**
 * Canonical stored account roles (D1 `accounts.role`).
 * Preserve these seven values — API and clients import from here.
 */
export const STORED_USER_ROLES = [
  "super_admin",
  "admin",
  "3d_designer",
  "moderator",
  "user",
  "licensed",
  "demo",
] as const;

export type StoredUserRole = (typeof STORED_USER_ROLES)[number];

/** Alias for historical imports. */
export type UserRole = StoredUserRole;

export const storedUserRoleSchema = z.enum(STORED_USER_ROLES, {
  errorMap: () => ({ message: "Invalid role" }),
});

export const isStoredUserRole = (value: string): value is StoredUserRole =>
  (STORED_USER_ROLES as readonly string[]).includes(value);

/**
 * Synthetic auth principal for API-key paths — never a D1 account role.
 * Must stay out of STORED_USER_ROLES / storedUserRoleSchema.
 */
export const SYNTHETIC_API_ROLE = "api" as const;
export type SyntheticApiRole = typeof SYNTHETIC_API_ROLE;

export const isSyntheticApiRole = (value: string): value is SyntheticApiRole =>
  value === SYNTHETIC_API_ROLE;

export const DASHBOARD_ROLES = ["super_admin", "admin"] as const;
export type DashboardRole = (typeof DASHBOARD_ROLES)[number];

export const STUDIO_ALLOWED_ROLES = [
  "super_admin",
  "admin",
  "3d_designer",
] as const satisfies readonly StoredUserRole[];

/** Product bypass via privilege — never granted by group flags. `licensed` alone is not exempt. */
export const SUBSCRIPTION_EXEMPT_ROLES = [
  "demo",
  "admin",
  "super_admin",
] as const satisfies readonly StoredUserRole[];

export const isSubscriptionExemptRole = (role: string): boolean =>
  (SUBSCRIPTION_EXEMPT_ROLES as readonly string[]).includes(role);

export type StudioAllowedRole = (typeof STUDIO_ALLOWED_ROLES)[number];

export const isStudioAllowedRole = (role: string): role is StudioAllowedRole =>
  (STUDIO_ALLOWED_ROLES as readonly string[]).includes(role);

export const isDashboardRole = (role: string): boolean =>
  (DASHBOARD_ROLES as readonly string[]).includes(role);

/**
 * Non–super_admin actors cannot create or promote anyone to super_admin,
 * and cannot modify an existing super_admin account's role.
 */
export const assertActorMayAssignRole = (
  actorRole: string,
  nextRole: string,
  currentRole?: string | null,
): { ok: true } | { ok: false; code: "forbidden"; message: string } => {
  if (!isStoredUserRole(nextRole)) {
    return { ok: false, code: "forbidden", message: "Unknown role" };
  }
  if (actorRole !== "super_admin") {
    if (nextRole === "super_admin") {
      return {
        ok: false,
        code: "forbidden",
        message: "Only super_admin can assign super_admin",
      };
    }
    if (currentRole === "super_admin") {
      return {
        ok: false,
        code: "forbidden",
        message: "Only super_admin can modify a super_admin account",
      };
    }
  }
  return { ok: true };
};
